"use client";

import React from "react";
import { ArrowRight, ChevronRight, CircleCheck as CheckCircle2, FlaskConical, ShieldCheck, Sparkles } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const consultationPoints = [{
  id: 1,
  title: "Method Feasibility & Optimization",
  description: "Tailored spectroscopic and chromatographic feasibility testing for active samples."
}, {
  id: 2,
  title: "Regulatory Compliance Support",
  description: "Assistance aligned with Pharmacopoeia and GMP data integrity standards."
}, {
  id: 3,
  title: "Instrumentation Selection",
  description: "Vendor-neutral technology advice matched to your specific analytical workflow."
}];
const keyStats = [{
  id: 1,
  value: "20+",
  label: "Years in Analytical Tech"
}, {
  id: 2,
  value: "",
  label: "Regulated Lab Focus"
}, {
  id: 3,
  value: "IE & UK",
  label: "On-site Direct Support"
}];
export default function ConsultationHero() {
  return <section id="consultationhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Subtle Background Accent Pattern */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center overflow-hidden opacity-30">
        <div className="h-[42rem] w-[42rem] rounded-full bg-secondary/80 blur-3xl" />
        <div className="h-[28rem] w-[28rem] rounded-full bg-accent/60 blur-2xl" />
      </div>

      <div className="page-container">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center text-xs text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-1.5 break-words">
            <li className="inline-flex items-center gap-1.5">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Link to="/ServicesPage" className="transition-colors hover:text-foreground">
                Services
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <span className="font-medium text-foreground" aria-current="page">
                Application Consultation
              </span>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Heading & Core Details */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }}>
              <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-medium gap-1.5 rounded-full">
                <Sparkles className="size-3.5 text-primary" />
                <span>Expert Analytical Advisory</span>
              </Badge>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Application Consultation for Regulated Laboratories
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Application-led analytical technology, qualification, and lifecycle support across Ireland and the UK. Our specialists help you select, validate, and deploy the right laboratory instrumentation.
            </motion.p>

            {/* CTAs */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="mt-8 flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="rounded-md shadow-sm font-medium">
                <Link to="/ContactPage">
                  <span>Book a Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/SolutionsPage">
                  <span>Explore Solutions</span>
                </Link>
              </Button>
            </motion.div>

            {/* Feature Checkpoints */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full pt-6 border-t border-border">
              {keyStats.map((stat, i) => <div key={stat.id} data-index={i} className="flex flex-col">
                  {stat.value && <span className="font-default text-2xl font-bold text-foreground">
                      {stat.value}
                    </span>}
                  <span className="text-xs text-muted-foreground mt-0.5">
                    {stat.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Right Column: Interactive Card & Visual Overview */}
          <div className="lg:col-span-5 relative">
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative mx-auto w-full max-w-lg">
              {/* Main Visual Card */}
              <Card className="border-border bg-card shadow-md overflow-hidden rounded-xl">
                <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_consultation_process.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_consultation_process.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_consultation_process.webp-wvc-sizes" alt="Analytical scientist reviewing lab results and qualification data" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-linear-to-t from-background/90 via-transparent to-transparent" />
                  
                  {/* Floating Micro Badge */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-lg bg-card/95 p-3 shadow-sm border border-border backdrop-blur-xs">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <FlaskConical className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Laboratory Feasibility</p>
                        <p className="text-[11px] text-muted-foreground">Sample testing & protocol validation</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase font-semibold">
                      Active
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6 space-y-4">
                  <h2 className="font-default text-base font-semibold text-foreground">
                    Consultation Deliverables
                  </h2>
                  <div className="space-y-3">
                    {consultationPoints.map((point, i) => <div key={point.id} data-index={i} className="flex items-start gap-3 text-left">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-foreground leading-snug">
                            {point.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                            {point.description}
                          </p>
                        </div>
                      </div>)}
                  </div>

                  <div className="pt-2">
                    <Button asChild variant="secondary" size="sm" className="w-full text-xs font-medium justify-center">
                      <Link to="/ServicesPage">
                        <span>View All Technical Services</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Decorative Secondary Floating Chip */}
              <div className="hidden sm:flex absolute -bottom-5 -left-5 items-center gap-2 rounded-lg bg-card px-3.5 py-2.5 shadow-md border border-border">
                <ShieldCheck className="size-4 text-primary" />
                <span className="text-xs font-medium text-card-foreground">
                  Data Integrity Focused
                </span>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}